import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useLang } from "@/i18n";

const ACTIVE_STATES = ["active", "canceled_active"];
const POLL_MS = 3000;
const MAX_POLLS = 30;

export default function BillingSuccess() {
  const { tf } = useLang();
  const [status, setStatus] = useState(null);
  const [phase, setPhase] = useState("verifying");
  const polls = useRef(0);

  useEffect(() => {
    let timer;
    let live = true;
    const poll = async () => {
      try {
        const r = await api.get("/billing/status");
        if (!live) return;
        setStatus(r.data);
        const d = r.data || {};
        const subscribed = ACTIVE_STATES.includes(d.state) ||
          (d.state === "trialing" && d.stripe_subscription_id);
        if (subscribed) { setPhase("confirmed"); return; }
      } catch { /* keep polling */ }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setPhase("pending"); return; }
      timer = setTimeout(poll, POLL_MS);
    };
    poll();
    return () => { live = false; clearTimeout(timer); };
  }, []);

  const retry = () => { polls.current = 0; setPhase("verifying"); window.location.reload(); };

  return (
    <div className="max-w-xl mx-auto py-16 px-4 text-center space-y-6" data-testid="billing-success-page">
      {phase === "verifying" && (
        <>
          <Loader2 className="h-12 w-12 mx-auto animate-spin text-athlynx" aria-hidden />
          <h1 className="text-2xl font-semibold tracking-tight">{tf("Vérification de votre abonnement…")}</h1>
          <p className="text-sm text-muted-foreground" data-testid="billing-success-verifying">
            {tf("Votre abonnement Athlynx est en cours d'activation. Merci de patienter quelques instants.")}
          </p>
        </>
      )}

      {phase === "confirmed" && (
        <>
          <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" aria-hidden />
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="billing-success-confirmed">{tf("Paiement confirmé")}</h1>
          <p className="text-sm text-muted-foreground">
            {tf("Votre abonnement est actif.")}{status?.plan_name ? ` ${status.plan_name} · ${tf(status.billing_interval === "annual" ? "Facturé annuellement" : "Facturé mensuellement")}` : ""}
          </p>
          <Button asChild className="gap-2" data-testid="billing-success-goto-billing">
            <Link to="/billing">{tf("Voir ma facturation")} <ArrowRight className="h-4 w-4" /></Link>
          </Button>
        </>
      )}

      {phase === "pending" && (
        <>
          <Clock className="h-12 w-12 mx-auto text-amber-500" aria-hidden />
          <h1 className="text-2xl font-semibold tracking-tight" data-testid="billing-success-pending">{tf("Activation en cours")}</h1>
          <p className="text-sm text-muted-foreground">
            {tf("L'activation prend plus de temps que prévu. Votre paiement est traité par Stripe et n'est pas perdu — revenez dans quelques minutes.")}
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button variant="outline" onClick={retry} data-testid="billing-success-retry">{tf("Réessayer la vérification")}</Button>
            <Button asChild variant="outline" data-testid="billing-success-back">
              <Link to="/billing">{tf("Voir ma facturation")}</Link>
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
